import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { isOwner } from "../../utils/roles";
import {
  fetchNotifications,
  fetchUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../api/NotificationAPIs";
import { enableOrderNotifications, pushEnabledLocally } from "../../notifications/pushNotifications";

function timeAgo(value) {
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(diff / 60000));
  if (minutes < 1) return "الآن";
  if (minutes < 60) return `منذ ${minutes} د`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `منذ ${hours} س`;
  return `منذ ${Math.floor(hours / 24)} ي`;
}

function severityClass(severity) {
  if (severity === "CRITICAL") return "border-rose-300 bg-rose-50/60";
  if (severity === "WARNING") return "border-amber-300 bg-amber-50/50";
  return "border-slate-200 bg-white";
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState(pushEnabledLocally() ? "granted" : "idle");
  const [pushError, setPushError] = useState("");
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const buttonRef = useRef(null);
  const owner = isOwner(authState.role);

  const unreadQuery = useQuery({
    queryKey: ["notification-unread"],
    queryFn: fetchUnreadNotificationCount,
    enabled: !!authState.token,
    refetchInterval: 30000,
  });

  const listQuery = useQuery({
    queryKey: ["notification-preview"],
    queryFn: () => fetchNotifications({ page: 0, pageSize: 8 }),
    enabled: open && !!authState.token,
  });

  useEffect(() => {
    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: ["notification-unread"] });
      queryClient.invalidateQueries({ queryKey: ["notification-preview"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    };
    window.addEventListener("girabat-notification", refresh);
    return () => window.removeEventListener("girabat-notification", refresh);
  }, [queryClient]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const unread = unreadQuery.data || 0;
  const items = listQuery.data?.items || [];

  const openItem = async (item) => {
    if (!item.isRead) {
      await markNotificationRead(item.notificationId);
      queryClient.invalidateQueries({ queryKey: ["notification-unread"] });
      queryClient.invalidateQueries({ queryKey: ["notification-preview"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    }
    setOpen(false);
    if (item.actionUrl) navigate(item.actionUrl);
  };

  const markAll = async () => {
    await markAllNotificationsRead();
    queryClient.invalidateQueries({ queryKey: ["notification-unread"] });
    queryClient.invalidateQueries({ queryKey: ["notification-preview"] });
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
  };

  const enablePush = async () => {
    setPushError("");
    try {
      const result = await enableOrderNotifications();
      setPushState(result.status);
      if (result.status === "denied") setPushError("تم رفض الإذن من المتصفح. يمكن تفعيله من إعدادات الموقع.");
      else if (result.status === "missing-vapid") setPushError("مفتاح Web Push غير مضاف بعد على الخادم.");
      else if (result.status === "unsupported") setPushError("هذا المتصفح لا يدعم إشعارات الويب.");
      else if (result.status !== "granted") setPushError("لم يتم تفعيل الإشعارات.");
    } catch (error) {
      setPushError(error.message || "تعذر تسجيل الجهاز.");
    }
  };

  const panel = open
    ? createPortal(
        <div className="fixed inset-0 z-[100]" dir="rtl">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            aria-label="إغلاق الإشعارات"
            onClick={() => setOpen(false)}
          />
          <div
            className={
              isMobile
                ? "absolute inset-x-3 top-16 bottom-3 flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
                : "absolute top-20 w-[360px] max-h-[min(32rem,calc(100vh-6rem))] flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
            }
            style={isMobile ? undefined : desktopPanelStyle(buttonRef.current)}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 shrink-0">
              <p className="font-bold text-slate-800 text-sm">الإشعارات</p>
              <button type="button" onClick={markAll} className="text-xs text-indigo-600 hover:text-indigo-800 shrink-0">
                تعليم الكل كمقروء
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              {listQuery.isLoading && <p className="px-4 py-6 text-sm text-slate-500">جارٍ التحميل...</p>}
              {!listQuery.isLoading && items.length === 0 && (
                <p className="px-4 py-6 text-sm text-slate-500">لا توجد إشعارات.</p>
              )}
              {items.map((item) => (
                <button
                  key={item.notificationId}
                  type="button"
                  onClick={() => openItem(item)}
                  className={`w-full text-right px-4 py-3 border-b border-slate-100 border-r-4 ${severityClass(item.severity)} ${
                    item.isRead ? "opacity-70" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-800 break-words">{item.title}</p>
                    <span className="text-[11px] text-slate-400 shrink-0">{timeAgo(item.createdAtUtc)}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1 leading-5 break-words">{item.body}</p>
                </button>
              ))}
            </div>

            <div className="px-4 py-3 bg-slate-50 space-y-2 shrink-0">
              {owner && pushState !== "granted" && (
                <button
                  type="button"
                  onClick={enablePush}
                  className="w-full rounded-xl bg-indigo-600 text-white text-sm font-semibold py-2.5 hover:bg-indigo-700"
                >
                  تفعيل إشعارات الطلبات
                </button>
              )}
              {owner && pushState === "granted" && (
                <p className="text-xs text-emerald-700 text-center">إشعارات هذا الجهاز مفعّلة</p>
              )}
              {!owner && (
                <p className="text-xs text-slate-500 text-center">تنبيهات العمل تصل إلى المالك فقط.</p>
              )}
              {pushError && <p className="text-xs text-rose-600 text-center break-words">{pushError}</p>}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/notifications");
                }}
                className="w-full text-sm text-slate-700 hover:text-indigo-700 py-1"
              >
                عرض كل الإشعارات
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
        aria-label="الإشعارات"
        aria-expanded={open}
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute top-0 left-0 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-[18px] text-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {panel}
    </div>
  );
}

function desktopPanelStyle(button) {
  const width = 360;
  const rect = button?.getBoundingClientRect();
  const top = rect ? rect.bottom + 8 : 80;
  let left = rect ? rect.left : 16;
  if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
  if (left < 8) left = 8;
  return { left, top };
}
